export type HelpLanguageRecord = {
  id?: number;
  helpContentId?: number;
  languageId?: string | number;
  title?: string;
  content?: string;
  detailContent?: string;
};

export type HelpContentFormat = 0 | 1;

const base64Pattern = /^[A-Za-z0-9+/]+={0,2}$/;

function normalizeBase64(value: string): string {
  return value.replace(/=+$/, "");
}

function decodeUtf8Base64(value: string): string {
  const binary = atob(value);
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function encodeUtf8Base64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

export function decodeHelpText(value?: string | null): string {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (trimmed.length % 4 !== 0) return value;
  if (!base64Pattern.test(trimmed)) return value;

  try {
    const decoded = decodeUtf8Base64(trimmed);
    const reEncoded = encodeUtf8Base64(decoded);
    if (normalizeBase64(reEncoded) === normalizeBase64(trimmed)) {
      return decoded;
    }
  } catch {
    return value;
  }

  return value;
}

export function encodeHelpText(value?: string | null): string {
  return encodeUtf8Base64(value ?? "");
}

export function normalizeHelpFormat(raw?: number | string | null): HelpContentFormat {
  if (raw === null || raw === undefined || raw === "") return 0;
  if (typeof raw === "number") return raw === 1 ? 1 : 0;
  const normalized = String(raw).trim().toUpperCase();
  if (normalized === "1" || normalized === "HTML") return 1;
  return 0;
}

export function readHelpEntryFormat(entry: unknown): HelpContentFormat {
  if (!entry || typeof entry !== "object") return 0;
  const record = entry as Record<string, unknown>;
  const raw = record.format ?? record.Format ?? record.contentFormat ?? record.ContentFormat;
  return normalizeHelpFormat(raw as number | string | null | undefined);
}

/** Title is always stored as base64-encoded plain text. */
export function encodeHelpTitleForStorage(value?: string | null): string {
  return encodeHelpText(value);
}

/** Text help: base64 plain text. HTML help: base64-encoded HTML source. */
export function encodeHelpContentForStorage(value: string | null | undefined, format: HelpContentFormat): string {
  if (format === 1) {
    return encodeHelpText(value);
  }
  return encodeHelpText(value);
}

/** Long-form help HTML stored as base64-encoded HTML source. */
export function encodeHelpDetailContentForStorage(value?: string | null): string {
  return encodeHelpText(value);
}

export function decodeHelpLanguage(language?: HelpLanguageRecord | null): HelpLanguageRecord {
  if (!language) return {};

  const record = language as HelpLanguageRecord & Record<string, unknown>;
  const languageId = record.languageId ?? record.LanguageId;
  const id = record.id ?? record.Id;
  const helpContentId = record.helpContentId ?? record.HelpContentId;

  return {
    ...language,
    id: typeof id === "number" ? id : undefined,
    helpContentId: typeof helpContentId === "number" ? helpContentId : undefined,
    languageId: (typeof languageId === "number" || typeof languageId === "string")
      ? languageId
      : undefined,
    title: decodeHelpText(String(record.title ?? record.Title ?? "")),
    content: decodeHelpText(String(record.content ?? record.Content ?? "")),
    detailContent: decodeHelpText(String(record.detailContent ?? record.DetailContent ?? "")),
  };
}
